/* Primeira etapa: modelo de apresentação. Nenhuma operação de rede ou storage. */
export const ORIGINS = [
  { id: 'self', label: 'Autoavaliação', short: 'Auto', color: '#6366f1' },
  { id: 'manager', label: 'Gestor', short: 'Gestor', color: '#0369a1' },
  { id: 'peer', label: 'Pares', short: 'Pares', color: '#0f766e' },
  { id: 'report', label: 'Liderados', short: 'Liderados', color: '#a16207' },
  { id: 'client', label: 'Clientes', short: 'Clientes', color: '#9333ea' },
];
export const SCALE = [
  { value: 1, label: 'Nunca' }, { value: 2, label: 'Raramente' },
  { value: 3, label: 'Às vezes' }, { value: 4, label: 'Frequentemente' },
  { value: 5, label: 'Sempre' }, { value: 'na', label: 'Não observei' },
];
const INTERNAL_ITEMS = [
  ['communication', 'Comunicação', 'Compartilhou informações com clareza.'],
  ['collaboration', 'Colaboração', 'Colaborou com as pessoas envolvidas nas entregas.'],
  ['commitment', 'Compromisso', 'Cumpriu os prazos combinados para suas entregas.'],
];
const CLIENT_ITEMS = [
  ['communication', 'Comunicação', 'Explicou as informações de forma clara e compreensível.'],
  ['understanding', 'Entendimento', 'Demonstrou compreender a necessidade apresentada.'],
  ['commitment', 'Compromisso', 'Cumpriu os prazos combinados para suas entregas.'],
  ['resolution', 'Encaminhamento', 'Encaminhou as demandas ao responsável adequado quando necessário.'],
  ['respect', 'Respeito', 'Tratou as pessoas com respeito durante o atendimento.'],
];
export const normalizeSearch = value => String(value ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
let sequence = 0;
export const makeId = prefix => `${prefix}-${typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${++sequence}-${Math.random().toString(36).slice(2)}`}`;
export const localDate = (date = new Date()) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
export const formatDate = value => {
  if (!value) return 'Não definido';
  const date = new Date(`${String(value).slice(0, 10)}T12:00:00`);
  return Number.isNaN(date.getTime()) ? 'Não definido' : date.toLocaleDateString('pt-BR');
};
export const safeColor = value => {
  const color = /^#(?:[a-f\d]{3}|[a-f\d]{6})$/i.test(String(value)) ? String(value) : '#64748b';
  return color.length === 4 ? `#${color.slice(1).split('').map(char => char + char).join('')}` : color;
};
export const initials = name => String(name || '?').trim().split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toUpperCase();
const userIdOf = data => {
  const id = data.userId !== undefined ? data.userId : data.user_id;
  return id == null || id === false || ['', '0'].includes(String(id).trim()) || /^custom-/i.test(String(id)) ? null : id;
};
const competencyNames = data => [...new Set([
  ...(Array.isArray(data.competenciasTecnicas) ? data.competenciasTecnicas : []),
  ...(Array.isArray(data.outrasCompetencias) ? data.outrasCompetencias : []),
].map(item => typeof item === 'string' ? item : item?.name || item?.nome || '').filter(Boolean))];

// Recebe apenas os nós que a tela já autorizou. Não lê fotos, nomes ou vínculos de outra unidade.
export function build360People(nodes = [], edges = [], unitId) {
  const parentByNode = new Map(edges.map(edge => [String(edge.target), String(edge.source)]));
  const nodeToPerson = new Map();
  const people = new Map();
  nodes.forEach(node => {
    const data = node.data || {};
    if (String(data.unit_id ?? data.unitId ?? '') !== String(unitId)) return;
    if (data.status === 'pending') return;
    const userId = userIdOf(data);
    const oldId = data.userId !== undefined ? data.userId : data.user_id;
    const nominal = typeof data.nome_customizado === 'string' ? data.nome_customizado.trim() :
      /^custom-/i.test(String(oldId)) ? String(data.nome || data.name || '').trim() : '';
    if (userId === null && (!nominal || ['Vaga Aberta', 'Vaga Pendente'].includes(nominal))) return;
    const occupant = data.nominalOccupantId ?? data.nominal_evaluation_data?.occupantId;
    const personId = data.personId ?? data.person_id;
    // session-node é somente fallback visual. O backend precisará fornecer personId persistente.
    const id = personId != null ? `person:${personId}` : userId !== null ? `user:${userId}` :
      occupant ? `nominal:${occupant}` : `session-node:${node.id}`;
    nodeToPerson.set(String(node.id), id);
    if (people.has(id)) { people.get(id).sourceNodeIds.push(String(node.id)); return; }
    people.set(id, {
      id, sourceNodeId: String(node.id), sourceNodeIds: [String(node.id)], userId, personId: personId ?? null,
      nominalOccupantId: occupant ?? null,
      name: nominal || data.nome || data.name || 'Nome não informado',
      role: data.cargo || data.role || 'Cargo não definido',
      area: data.areaName || data.area || 'Área não definida',
      color: safeColor(data.color), avatar: data.profile_picture_url || data.avatarUrl || '',
      competencies: competencyNames(data), managerId: null,
    });
  });
  people.forEach(person => { person.managerId = nodeToPerson.get(parentByNode.get(person.sourceNodeId)) || null; });
  return [...people.values()].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'));
}
export const createQuestion = (audience = 'internal') => ({ id: makeId('question'), audience, competency: '', text: '' });
export function createDraft(unitId, unitName, initialPerson) {
  const now = new Date();
  const end = localDate(now);
  const start = localDate(new Date(now.getFullYear(), now.getMonth(), 1));
  const deadline = localDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 14));
  return {
    id: makeId('cycle'), unitId: String(unitId), unitName, name: '', purpose: 'Desenvolvimento por competências',
    start, end, deadline, status: 'draft', isDemo: false, origins: ['self', 'manager', 'peer', 'client'],
    participants: initialPerson ? [{ ...initialPerson, competencies: [...initialPerson.competencies] }] : [],
    assignments: [], confidentiality: 'grouped', minResponses: 3,
    questions: [
      ...INTERNAL_ITEMS.map(([id, competency, text]) => ({ id: `internal-${id}`, audience: 'internal', competency, text })),
      ...CLIENT_ITEMS.map(([id, competency, text]) => ({ id: `client-${id}`, audience: 'client', competency, text })),
    ],
  };
}
export function toggleParticipant(draft, person) {
  const removing = draft.participants.some(item => item.id === person.id);
  return { ...draft,
    participants: removing ? draft.participants.filter(item => item.id !== person.id) : [...draft.participants, { ...person, competencies: [...person.competencies] }],
    assignments: removing ? draft.assignments.filter(item => item.participantId !== person.id) : draft.assignments,
  };
}
export function setOrigins(draft, origins) {
  return { ...draft, origins, assignments: draft.assignments.filter(item => origins.includes(item.origin)) };
}
export function questionsFor(cycle, origin) {
  return cycle.questions.filter(item => item.audience === (origin === 'client' ? 'client' : 'internal'));
}
export function addAssignment(draft, input) {
  const participant = draft.participants.find(item => item.id === input.participantId);
  if (!participant) return { error: 'Selecione a pessoa que será avaliada.' };
  if (!draft.origins.includes(input.origin)) return { error: 'Ative essa origem de avaliação antes de incluir o avaliador.' };
  const external = input.origin === 'client';
  const email = String(input.email || '').trim().toLowerCase();
  const name = String(external ? input.name : input.reviewer?.name || '').trim();
  if (external && (!name || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))) return { error: 'Informe o nome e um e-mail válido do cliente.' };
  if (!external && !input.reviewer?.id) return { error: 'Selecione um avaliador da equipe.' };
  const reviewerId = external ? `client:${email}` : input.reviewer.id;
  if (!external && ((input.origin === 'self') !== (reviewerId === participant.id))) {
    return { error: input.origin === 'self' ? 'A autoavaliação deve ser respondida pela própria pessoa.' : 'Para avaliar a si mesmo, use a origem Autoavaliação.' };
  }
  if (draft.assignments.some(item => item.participantId === participant.id && item.reviewerId === reviewerId)) {
    return { error: 'Esse avaliador já foi incluído para esta pessoa neste ciclo.' };
  }
  return { assignment: {
    id: makeId('assignment'), participantId: participant.id, reviewerId, origin: input.origin,
    reviewerName: name, email: external ? email : '', company: external ? String(input.company || '').trim() : '',
    context: String(input.context || '').trim(), status: 'planned', answers: {},
  } };
}
export function suggestAssignments(draft, people) {
  const result = { ...draft, assignments: [...draft.assignments] };
  draft.participants.forEach(person => {
    const candidates = [
      { origin: 'self', reviewer: person },
      { origin: 'manager', reviewer: people.find(item => item.id === person.managerId) },
    ];
    candidates.forEach(candidate => {
      if (!candidate.reviewer || !draft.origins.includes(candidate.origin)) return;
      const added = addAssignment(result, { participantId: person.id, ...candidate });
      if (added.assignment) result.assignments.push(added.assignment);
    });
  });
  return result;
}
const isDate = value => /^\d{4}-\d{2}-\d{2}$/.test(value || '') && localDate(new Date(`${value}T12:00:00`)) === value;
export function validateStep(cycle, step) {
  if (step === 0) {
    if (!cycle.name.trim()) return 'Dê um nome ao ciclo.';
    if (![cycle.start, cycle.end, cycle.deadline].every(isDate)) return 'Informe datas válidas para o período e o prazo.';
    if (cycle.start > cycle.end) return 'O início do período deve ser anterior ou igual ao fim.';
    if (cycle.deadline < cycle.end) return 'O prazo de resposta deve ser igual ou posterior ao fim do período avaliado.';
    if (cycle.deadline < localDate()) return 'O prazo de resposta não pode estar no passado.';
  }
  if (step === 2) {
    if (!cycle.origins.length) return 'Selecione pelo menos uma origem de avaliação.';
    for (const audience of [...new Set(cycle.origins.map(origin => origin === 'client' ? 'client' : 'internal'))]) {
      const questions = cycle.questions.filter(item => item.audience === audience);
      if (!questions.length) return `Inclua ao menos uma pergunta para ${audience === 'client' ? 'clientes' : 'a equipe'}.`;
      if (questions.some(item => !item.text.trim() || !item.competency.trim())) return 'Preencha o texto e a competência de cada pergunta.';
    }
  }
  return '';
}
export function launchIssues(cycle) {
  const issues = [validateStep(cycle, 0), validateStep(cycle, 2)].filter(Boolean);
  if (!cycle.participants.length) issues.push('Selecione ao menos um colaborador.');
  cycle.participants.forEach(person => {
    if (!cycle.assignments.some(item => item.participantId === person.id)) issues.push(`${person.name}: selecione ao menos um avaliador.`);
  });
  return issues;
}
export const isValidAnswer = value => value === 'na' || (Number.isInteger(value) && value >= 1 && value <= 5);
// Agrega por pergunta; auto/gestor identificáveis. Não deriva resultados do histórico legado.
export function aggregateQuestion(cycle, participantId, questionId, origin) {
  const uniqueReviewers = new Map();
  cycle.assignments.filter(item => item.participantId === participantId && item.origin === origin && item.status === 'submitted')
    .forEach(item => uniqueReviewers.set(item.reviewerId, item));
  const values = [...uniqueReviewers.values()].map(item => item.answers?.[questionId]);
  const scores = values.filter(value => typeof value === 'number' && isValidAnswer(value));
  const minimum = cycle.confidentiality === 'grouped' && ['peer', 'report', 'client'].includes(origin) ? cycle.minResponses : 1;
  const suppressed = scores.length < minimum;
  return { value: suppressed ? null : scores.reduce((sum, n) => sum + n, 0) / scores.length,
    valid: scores.length, notObserved: values.filter(value => value === 'na').length, suppressed, minimum };
}
export const cycleCounts = cycle => ({
  participants: cycle.participants.length, assignments: cycle.assignments.length,
  submitted: cycle.assignments.filter(item => item.status === 'submitted').length,
  clients: cycle.assignments.filter(item => item.origin === 'client').length,
});
export function createDemo(unitId, unitName) {
  const person = { id: 'demo:ana', sourceNodeId: 'demo-node', name: 'Ana Martins', role: 'Analista de relacionamento', area: 'Atendimento', color: '#0f766e', avatar: '', competencies: ['Comunicação'], managerId: 'demo:renato' };
  const cycle = { ...createDraft(unitId, unitName, person), name: 'Relacionamento com clientes · Exemplo', isDemo: true, status: 'demo' };
  const reviewers = [
    ['self', 'Ana Martins', 4], ['manager', 'Renato Lima', 4],
    ['peer', 'Beatriz Souza', 3], ['peer', 'Marcos Alves', 4], ['peer', 'Paula Dias', 5],
    ['client', 'Cliente A', 5], ['client', 'Cliente B', 4], ['client', 'Cliente C', 4],
  ];
  cycle.assignments = reviewers.map(([origin, reviewerName, rating], i) => ({
    id: `demo-assignment-${i}`, participantId: person.id, reviewerId: origin === 'self' ? person.id : origin === 'manager' ? person.managerId : `demo-reviewer-${i}`, reviewerName, origin,
    email: origin === 'client' ? `cliente${i}@example.com` : '', company: origin === 'client' ? 'Empresa de exemplo' : '',
    context: 'Atendimento durante o período', status: 'submitted',
    answers: Object.fromEntries(questionsFor(cycle, origin).map((question, q) => [question.id, i === 7 && q === 3 ? 'na' : rating])),
  }));
  return cycle;
}
